import { useState } from "react";
import type { PushJobMode } from "@tg/shared";
import type { PushJobItemView } from "../../lib/push-api.js";
import { useUiPrefs } from "../../lib/ui-prefs.js";
import { formatFullDateTime } from "../../lib/datetime.js";
import { PUSH_ERROR_LABELS, PUSH_SKIP_LABELS, pushItemStatusLabel } from "./push-labels.js";

// Разбор пропуша по чатам (журнал в настройках, только менеджерам): для каждого
// чата — статус, что сгенерировал ИИ, и раскрываемый блок «Что ушло в ИИ»:
// system-промпт (промпт админа + жёсткие правила, которые дописывает код) и
// транскрипт с метками OUR/USER — ровно в том виде, в каком его увидела модель.

// Заголовок блока жёстких правил — тот же, что в gateway llm-push.ts.
const HARD_RULES_HEADER = "--- HARD RULES (override anything above) ---";

const reasonLabel = (item: PushJobItemView): string | null => {
  if (!item.reason) return null;
  if (item.status === "skipped") {
    return PUSH_SKIP_LABELS[item.reason as keyof typeof PUSH_SKIP_LABELS] ?? item.reason;
  }
  if (item.status === "error") {
    const [code, ...rest] = item.reason.split(":");
    const label = PUSH_ERROR_LABELS[code as keyof typeof PUSH_ERROR_LABELS];
    return label ? (rest.length > 0 ? `${label} — ${rest.join(":").trim()}` : label) : item.reason;
  }
  return item.reason;
};

const SystemPromptView = ({ prompt }: { prompt: string }) => {
  const index = prompt.indexOf(HARD_RULES_HEADER);
  const adminPart = index >= 0 ? prompt.slice(0, index).trimEnd() : prompt;
  const rulesPart = index >= 0 ? prompt.slice(index) : "";
  return (
    <div className="push-llm__system">
      <div className="push-llm__block push-llm__block--admin">
        <div className="push-llm__label">Промпт админа (из настроек аккаунта)</div>
        <pre>{adminPart.length > 0 ? adminPart : "— пусто —"}</pre>
      </div>
      {rulesPart ? (
        <div className="push-llm__block push-llm__block--rules">
          <div className="push-llm__label">Жёсткие правила — дописывает система, перекрывают промпт</div>
          <pre>{rulesPart}</pre>
        </div>
      ) : null}
    </div>
  );
};

const LlmInputView = ({ item }: { item: PushJobItemView }) => {
  const timeZone = useUiPrefs((state) => state.timeZone);
  const input = item.llmInput;
  if (!input) return null;
  return (
    <div className="push-llm">
      <div className="push-llm__section">
        <div className="push-llm__heading">
          <span>system</span>
          <small>1 сообщение</small>
        </div>
        {item.llmSystemPrompt ? <SystemPromptView prompt={item.llmSystemPrompt} /> : null}
      </div>
      <div className="push-llm__section">
        <div className="push-llm__heading">
          <span>user</span>
          <small>
            JSON: our_name «{input.our_name}», user_name «{input.user_name}», transcript —{" "}
            {input.transcript.length} сообщ.
            {input.insisted ? " · был повторный запрос (пустой/дублирующий ответ)" : ""}
          </small>
        </div>
        <ol className="push-llm__transcript">
          {input.transcript.map((entry, index) => (
            <li
              key={index}
              className={`push-llm__msg push-llm__msg--${entry.from === "OUR" ? "our" : "user"}`}
            >
              <div className="push-llm__msg-head">
                <span className="push-llm__from">{entry.from}</span>
                <span className="push-llm__who">{entry.from === "OUR" ? input.our_name : input.user_name}</span>
                <time>{formatFullDateTime(entry.at, timeZone)}</time>
              </div>
              <div className="push-llm__msg-text">{entry.text}</div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export const PushItemsReport = ({
  items,
  chatTitle,
  mode = "ai"
}: {
  items: PushJobItemView[];
  mode?: PushJobMode;
  /** Имя чата, если сервер не отдал userName (чужой запуск у саппорта). */
  chatTitle?: (chatId: string) => string | undefined;
}) => {
  const [openChatId, setOpenChatId] = useState<string | null>(null);
  if (items.length === 0) {
    return <div className="settings-card__hint">Подходящих чатов не нашлось.</div>;
  }
  return (
    <ul className="push-items">
      {items.map((item) => {
        const reason = reasonLabel(item);
        const open = openChatId === item.chatId;
        const name = item.userName || chatTitle?.(item.chatId) || `Чат ${item.chatId}`;
        return (
          <li key={item.chatId} className={`push-items__row push-items__row--${item.status}`}>
            <div className="push-items__head">
              <span className="push-items__pos">{item.position + 1}</span>
              <strong className="push-items__name">{name}</strong>
              <span className={`push-items__status push-items__status--${item.status}`}>
                {pushItemStatusLabel(item.status, mode)}
              </span>
              {reason ? <span className="push-items__reason">{reason}</span> : null}
              {item.llmInput ? (
                <button
                  type="button"
                  className="button button--ghost push-items__toggle"
                  onClick={() => setOpenChatId(open ? null : item.chatId)}
                >
                  {open ? "Скрыть запрос" : "Что ушло в ИИ"}
                </button>
              ) : null}
            </div>
            {item.generatedText ? (
              <div className="push-items__generated">
                <span className="push-llm__label">
                  {mode === "scripts" ? "Текст" : "Ответ ИИ"}
                  {item.status === "sent" ? " (отправлен)" : ""}
                </span>
                <div>{item.generatedText}</div>
              </div>
            ) : null}
            {open ? <LlmInputView item={item} /> : null}
          </li>
        );
      })}
    </ul>
  );
};
